<script lang="ts">
  import { default as CraftNavigation } from "@modern/components/retailer/Navigation/ProductLinesNavigation.svelte";
  import ProductListingPage from "@retailer/svelte/ProductLines/Index/ProductListingPage.svelte";
  import Spinner from "@shared/svelte/Components/SpinnerInternal.svelte";
  import { getControlStateAuthenticationService } from "@retailer/services/retailer_user_authentications_service";

  import RetailerFulfillmentMethodsStore from "@retailer/svelte/stores/RetailerFulfillmentMethodsStore";
  import StateSettingsStore from "@shared/svelte/Stores/StateSettingsStore";

  import cart from "@modern/services/cart";
  import getFilterParamsFromState from "@retailer/services/filter_params_from_state_params.js";
  import Flash from "@shared/services/flashv2.js";

  import type { ProductIndexBanner } from "@shared/types/ProductIndexBanner";
  import type { ShoppableUnit } from "@shared/types/ShoppableUnit";

  import { convertParamsToStateParams } from "@modern/services/angularjs_shims/state_params";
  import { getProductIndexBanners } from "@shared/services/banner_service";
  import { getProductListingPageNameAndValue } from "@retailer/services/page_location_helpers";
  import { getShoppableUnits } from "@shared/services/shoppable_unit_service";
  import { urlToParamsObject } from "@shared/svelte/utils";
  import { HttpPromise } from "@shared/svelte/Modules/ApiService";

  import { beforeUrlChange, params, page } from "@roxi/routify";
  import { each } from "lodash";

  const { pageName, pageValue } = getProductListingPageNameAndValue($params);

  const Cart = cart.getCartService();
  const authService = getControlStateAuthenticationService();

  let productIndexBanners: ProductIndexBanner[] = [];
  let shoppableUnit = {} as ShoppableUnit;

  const stateSettingsRes = async () => {
    let res = await HttpPromise({
      method: "GET",
      url: "/api/retailer/state_settings",
    });
    StateSettingsStore.set(res);
    return res;
  };

  const discoveryPanelRes = async () => {
    try {
      let res = await HttpPromise({
        method: "GET",
        url: "/api/retailer/discovery_panel/discovery_config",
        data: { page_name: pageName, page_value: pageValue },
      });
      return {
        data: res,
        pageName,
        pageValue,
      };
    } catch (e) {
      return {
        data: [],
        pageName,
        pageValue,
      };
    }
  };

  const load = async () => {
    const promises1 = [
      discoveryPanelRes(),
      stateSettingsRes(),
      getProductIndexBanners(pageName, pageValue, {
        ...getFilterParamsFromState($params),
        page: $params.page,
      }),
      getShoppableUnits(pageName, pageValue, {
        ...getFilterParamsFromState($params),
        page: $params.page,
      }),
      RetailerFulfillmentMethodsStore.refreshFulfillmentMethods(),
    ];

    let results1 = await Promise.all(promises1);

    productIndexBanners = results1[2];
    shoppableUnit = results1[3];

    if ($params.from_sf_product) {
      let message = "";

      if ($params.distributors) {
        message = `Apologies! The product you're searching for isn't available right now. Please explore other products from ${$params.distributor_name} or contact customer support for assistance.`;
      } else {
        message = `We regret to inform you the product you're trying to access is currently unavailable in your area${
          $params.distributor_name ? ` from ${$params.distributor_name}` : ""
        }. We apologize for any inconvenience caused and recommend exploring products from other distributors or contacting our customer support for further assistance. Thank you for your understanding.`;
      }

      Flash.now({
        type: "success",
        message: message,
        timeout: 10000,
      });
    }

    return {
      discoveryPanel: results1[0],
    };
  };

  const tags = [
    "t",
    "c",
    "s",
    "d",
    "n",
    "b",
    "m",
    "v",
    "r",
    "g",
    "wa",
    "wv",
    "co",
    "po",
    "dl",
    "md",
    "fe",
    "re",
    "z",
    "rm",
    "ap",
    "ins",
    "f",
    "categorySearch",
    "distributorSearch",
    "brandFamilySearch",
    "subcategorySearch",
    "productTypeSearch",
    "manufacturerSearch",
    "materialSearch",
    "vintageYearSearch",
    "varietalSearch",
    "appellationSearch",
    "countrySearch",
    "regionSearch",
    "tagSearch",
    "container_sizeSearch",
    "container_typeSearch",
  ];

  const refetchProductIndexBanners = async (newPageName, newPageValue, newParams) => {
    // Reset to page 1 when filters change to avoid page parameter persistence
    const paramsWithPageReset = { ...newParams, page: 1 };
    productIndexBanners = await getProductIndexBanners(
      newPageName,
      newPageValue,
      getFilterParamsFromState(paramsWithPageReset),
    );
  };

  const refetchShoppableUnits = async (newPageName, newPageValue, newParams) => {
    // Reset to page 1 when filters change to avoid page parameter persistence
    const paramsWithPageReset = { ...newParams, page: 1 };
    shoppableUnit = await getShoppableUnits(
      newPageName,
      newPageValue,
      getFilterParamsFromState(paramsWithPageReset),
    );
  };

  const shouldRefetchDisplayAds = (oldParams, newParams): boolean => {
    const displayFilterTags = ["t", "c", "s", "ap", "b", "co", "d", "rm", "re", "fe"];
    return displayFilterTags.some(t => oldParams[t] !== newParams[t]);
  };

  $beforeUrlChange((event, route) => {
    if (!event["url"]) {
      return true;
    }
    const newParams = urlToParamsObject(event["url"]);
    const oldParams = $params;
    const { pageName: newPageName, pageValue: newPageValue } =
      getProductListingPageNameAndValue(newParams);
    let shouldRefresh = true;
    if (newPageName === pageName && newPageValue === pageValue) {
      each(tags, function (t) {
        if (
          (newParams[t] && newParams[t] !== oldParams[t]) ||
          (oldParams[t] && oldParams[t] !== newParams[t])
        ) {
          shouldRefresh = false;
        }
      });
    }

    if (shouldRefresh) {
      if ($page["reset"]) {
        $page["reset"]();
      } else {
        window.location.href = event["url"];
      }
    } else if (shouldRefetchDisplayAds(oldParams, newParams)) {
      refetchProductIndexBanners(newPageName, newPageValue, newParams);
      refetchShoppableUnits(newPageName, newPageValue, newParams);
    }
    return true;
  });
</script>

<CraftNavigation />
{#await load()}
  <Spinner />
{:then data}
  <ProductListingPage
    bind:productIndexBannersRes={productIndexBanners}
    bind:shoppableUnitRes={shoppableUnit}
    stateParams={convertParamsToStateParams($params)}
    discoveryPanelRes={data.discoveryPanel}
    {Cart}
  />
  {#if authService?.authenticationModalComponent}
    <svelte:component this={authService?.authenticationModalComponent} />
  {/if}
{/await}
